"use client";

import { motion } from "framer-motion";
import { Plus, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { SAUCE_OPTIONS, PROTEIN_OPTIONS } from "@/lib/order-config";
import { formatPrice } from "@/stores/cart-store";
import { staggerContainer, listItem, pressScale } from "@/lib/animations";

interface PastaCustomizerProps {
  sauceAmount: string;
  onSauceChange: (value: string) => void;
  selectedProtein: string | null;
  onProteinChange: (value: string | null) => void;
}

export function PastaCustomizer({
  sauceAmount,
  onSauceChange,
  selectedProtein,
  onProteinChange,
}: PastaCustomizerProps) {
  return (
    <div className="space-y-6">
      {/* Sauce Amount Section */}
      <div className="space-y-4">
        <h4 className="font-semibold text-base text-foreground">
          Sauce Amount
        </h4>

        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="flex gap-2"
        >
          {SAUCE_OPTIONS.map((option) => {
            const isSelected = sauceAmount === option.value;

            return (
              <motion.button
                key={option.value}
                variants={listItem}
                {...pressScale}
	                type="button"
	                aria-pressed={isSelected}
	                onClick={() => onSauceChange(option.value)}
	                className={cn(
	                  "flex-1 h-11 rounded-full text-base font-semibold transition-all touch-manipulation active:scale-95 sm:h-10 sm:text-sm",
	                  "border",
	                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                  isSelected
                    ? "bg-foreground text-background border-foreground shadow-md"
                    : "bg-card text-card-foreground border-border hover:bg-muted"
                )}
              >
                {option.label}
              </motion.button>
            );
          })}
        </motion.div>
      </div>

      {/* Add Protein Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-base text-foreground">
            Add Protein
          </h4>
          <span className="text-xs text-muted-foreground font-normal">
            Optional
          </span>
        </div>

        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="flex gap-2"
        >
          {PROTEIN_OPTIONS.map((option) => {
            const isSelected = selectedProtein === option.value;

            return (
              <motion.button
                key={option.value}
                variants={listItem}
                {...pressScale}
	                type="button"
	                aria-pressed={isSelected}
	                onClick={() => onProteinChange(isSelected ? null : option.value)}
	                className={cn(
	                  "flex-1 h-16 rounded-xl text-base font-medium transition-all relative touch-manipulation active:scale-95 sm:h-14 sm:text-sm",
	                  "border flex flex-col items-center justify-center gap-0.5",
	                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                  isSelected
                    ? "bg-foreground text-background border-foreground shadow-md"
                    : "bg-card text-card-foreground border-border hover:bg-muted"
                )}
              >
                {/* Selection indicator */}
                {isSelected && (
                  <div className="absolute top-1.5 right-1.5">
                    <Check className="h-3 w-3 text-background" />
                  </div>
                )}
                {!isSelected && (
                  <div className="absolute top-1.5 right-1.5">
                    <Plus className="h-3 w-3 text-muted-foreground" />
                  </div>
                )}
                <span className="font-semibold">{option.label}</span>
                <span className={cn(
                  "text-xs",
                  isSelected ? "text-background/80" : "text-muted-foreground"
                )}>
                  +{formatPrice(option.price)}
                </span>
              </motion.button>
            );
          })}
        </motion.div>
      </div>
    </div>
  );
}
